import React, { useState, useEffect, useContext, useCallback } from 'react';
import { filter } from 'lodash';
import { Redirect } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import classNames from 'classnames';
import { Button, PageLoader, Notifier, notify } from '@scholastic/volume-react';

import ReportSelector from './ReportSelector/ReportSelector';
import ProductSelector from './ProductSelector/ProductSelector';
import DateSelector from './DateSelector/DateSelector';
import SiteSelector from './SiteSelector/SiteSelector';

import { TypeOfReport } from './ReportSelector/ReportType.data';

import { fetchProductInfo } from '../../state/Home.api';
import { ActionType, StoreContext } from '../../state/store';

import { OutputType } from './SiteSelector/SiteSelector';
import { sortDropdownDates } from '../../helper/CommonHelper';

import { dateToUTC } from '../../utils/dataparsers.util';

import {
  ProductsAPIResponse,
  getProductsFromAPIResponse,
  getCustomerInfoFromAPIResponse,
  SearchableDropdownValue,
  ReportChartsRequestProps,
  IndividualSiteRequestProps,
} from './Home.dataparser';

import {
  TransformChartDataResponse,
  ChartApiRequest,
  ChartApiResponse,
  transformChartData,
  transformGroupedChartData,
} from '../ReportCharts/ReportDataTransform';

import { fetchChartData } from '../../state/ReportsChart.api';
import { transformTableData } from '../ReportCharts/ReportChartBody/ReportTable/ReportTable.transformer';

import './Home.scss';

/**
 * This is "Home" Component. It contains all the children components and functionality for Homepage/Dashboard page.
 * @exports
 * @returns JSX.Element
 */
export const Home = () => {
  const [reportType, setReportType] = useState<TypeOfReport>('OSR');
  const [selectedProducts, setSelectedProducts] = useState<SearchableDropdownValue[] | undefined>();
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  const [isIndividualReport, setIsIndividualReport] = useState<boolean>(true);
  const setIsIndividualReportCallback = useCallback(
    (selectedValue: boolean) => {
      setIsIndividualReport(selectedValue);
    },
    [setIsIndividualReport],
  );

  const [selectedStartDate, setSelectedStartDate] = useState<string | undefined>();

  const { state, dispatch } = useContext(StoreContext);
  const [products, setProducts] = useState<ProductsAPIResponse | null>(null);

  const [parsedProducts, setParsedProducts] = useState<Array<SearchableDropdownValue>>([]);
  const [parsedDates, setParsedDates] = useState<Array<SearchableDropdownValue>>([]);

  const onDateSelectCallback = useCallback(
    (selectedValue: string | undefined) => {
      setSelectedDate(selectedValue);
    },
    [setSelectedDate],
  );

  const [isGroup, setGroupStatus] = useState<boolean>(false);
  const [hasChildren, setChildrenStatus] = useState<boolean>(false);

  const [outputType, setOutputType] = useState<OutputType | undefined>('Html');

  const onReportTypeSelectionCallback = useCallback(
    (outputType: OutputType) => {
      setOutputType(outputType);
    },
    [setOutputType],
  );

  const [allProductSelected, setAllProductSelected] = useState<boolean>(false);

  const [detailedCsvData, setDetailedCsvData] = useState<(string | number)[][]>([]);

  const [reportLoading, setReportLoading] = useState<boolean>(false);

  const getProducts = async (): Promise<void> => {
    try {
      const productsInfo: ProductsAPIResponse = await fetchProductInfo();
      setProducts(productsInfo);
    } catch (err) {
      notify.warning('Something went wrong, please try again later');
      console.error(err);
    }
  };

  const isDetailedReport =
    reportType === 'OUS' ||
    reportType === 'DDU' ||
    reportType === 'DSU' ||
    reportType === 'DUBG' ||
    reportType === 'OSR' ||
    reportType === 'SUBG';

  const customer = state.customerInfo;

  useEffect(() => {
    getProducts();
  }, []);

  useEffect(() => {
    const parseProducts = (): void => {
      if (customer && products) {
        const rawProducts: { [key: string]: string } | null = getProductsFromAPIResponse(products);
        const customerProducts: Array<string> | undefined =
          getCustomerInfoFromAPIResponse(customer)?.products;

        if (rawProducts && customerProducts) {
          const availableProducts: Array<SearchableDropdownValue> = customerProducts
            .filter((product: string) => {
              return !!rawProducts[product];
            })
            .map((product: string): SearchableDropdownValue => {
              return {
                value: product,
                label: rawProducts[product],
                isSelected: false,
              };
            });

          setParsedProducts(availableProducts);
        }
      }
    };

    const getDates = () => {
      if (customer) {
        const datesFromCustomer: { [key: string]: string } | undefined =
          getCustomerInfoFromAPIResponse(customer)?.usagedates;

        if (datesFromCustomer) {
          const availableDates: Array<SearchableDropdownValue> = [];

          // Will iterate trough pair object and will create formated array for the dropdown.
          for (let key in datesFromCustomer) {
            if (Object.prototype.hasOwnProperty.call(datesFromCustomer, key)) {
              // Restricts minimum date to 2016
              if (Number(key.split('-')[0]) >= 2016) {
                availableDates.push({
                  label: datesFromCustomer[key],
                  value: key,
                });
              }
            }
          }

          setParsedDates(sortDropdownDates(availableDates));
        }
      }
    };

    if (customer) {
      const customerGroups = getCustomerInfoFromAPIResponse(customer)?.groups;
      setGroupStatus(
        !!getCustomerInfoFromAPIResponse(customer)?.hasgroups && customerGroups
          ? !!customerGroups.length
          : false,
      );
      setChildrenStatus(!!getCustomerInfoFromAPIResponse(customer)?.haschildren);
    }

    getDates();
    parseProducts();
  }, [customer, products]);

  const preparePostRequest = (): ReportChartsRequestProps => {
    let endDate = selectedDate ? selectedDate : new Date();
    const usagesDateList = Object.keys(getCustomerInfoFromAPIResponse(customer)?.usagedates || {});

    const chosenProducts = selectedProducts && selectedProducts[0];

    const childrenCustomerList = [
      ...Object.keys(getCustomerInfoFromAPIResponse(customer)?.children || {}),
    ];

    let chartStartDate = dateToUTC(new Date(endDate));
    chartStartDate.setMonth(chartStartDate.getMonth() - 14);
    chartStartDate.setDate(1);

    const dateIndex = usagesDateList.indexOf(chartStartDate.toLocaleDateString('en-CA'), 0);

    if (dateIndex === -1) {
      chartStartDate = dateToUTC(new Date(usagesDateList[0]));
    }

    const filteredProduct = filter(
      getCustomerInfoFromAPIResponse(customer)?.products,
      (val) => val === chosenProducts?.value,
    );

    const postData: ReportChartsRequestProps = {
      loggedInUserName: getCustomerInfoFromAPIResponse(customer)?.customername,
      productName: chosenProducts?.label,
      startDate: chartStartDate,
      endDate: dateToUTC(new Date(endDate)),
      cuId: getCustomerInfoFromAPIResponse(customer)?.customerid,
      product: filteredProduct,
      customer: isIndividualReport
        ? childrenCustomerList.join(',')
        : getCustomerInfoFromAPIResponse(customer)?.customerid.toString(),
      outputType: outputType,
    };

    switch (reportType) {
      case 'SRISR':
        postData.endDate = selectedDate;
        postData.startDate = selectedStartDate ? selectedStartDate : parsedDates[0].value;
        postData.products = selectedProducts;
        break;
      case 'OSR':
        postData.reportType = 'usageSummary';
        postData.endDate = selectedDate;
        postData.startDate = selectedStartDate ? selectedStartDate : parsedDates[0].value;
        postData.products = selectedProducts;
        break;
      case 'UGR':
        postData.reportType = 'usageSummary';
        postData.endDate = selectedDate;
        postData.startDate = selectedStartDate ? selectedStartDate : parsedDates[0].value;
        postData.products = selectedProducts;
        break;
      case 'OUS':
        postData.reportType = 'usageSummary';
        break;
      case 'DDU':
        postData.reportType = 'documentUsage';
        postData.byProduct = 'N';
        postData.valueKey = 'documents';
        break;
      case 'DSU':
        postData.reportType = 'sessionUsage';
        postData.byProduct = 'N';
        postData.valueKey = 'sessions';
        break;
      case 'DUBG':
        postData.reportType = 'documentUsageByGroup';
        postData.byProduct = 'N';
        postData.valueKey = 'documents';
        break;
      case 'SUBG':
        postData.reportType = 'sessionUsageByGroup';
        postData.byProduct = 'N';
        postData.valueKey = 'documents';
        break;
      default:
        postData.reportType = 'usageSummary';
    }

    return postData;
  };

  const validateFields = (): boolean => {
    let isValid = true;
    if (!selectedProducts || selectedProducts.length === 0) {
      isValid = false;

      notify.warning('Create Report failed: Please select Product from the list');
    } else if (
      !selectedDate ||
      ((reportType === 'OSR' || reportType === 'UGR' || reportType === 'SRISR') &&
        !selectedStartDate)
    ) {
      isValid = false;

      notify.warning('Create Report failed: Please select Month/Year for report generation');
    } else if (
      (reportType === 'OSR' || reportType === 'UGR' || reportType === 'SRISR') &&
      selectedStartDate &&
      new Date(selectedDate) < new Date(selectedStartDate)
    ) {
      isValid = false;

      notify.warning('Create Report failed: To date must be greater or equal to From date');
    } else {
      isValid = true;
    }
    return isValid;
  };

  const createReport = (): void => {
    if (validateFields()) {
      const requestBody = preparePostRequest();
      if (
        requestBody.outputType === 'Html' ||
        reportType === 'UGR' ||
        reportType === 'OSR' ||
        reportType === 'DUBG' ||
        reportType === 'SUBG'
      ) {
        dispatch({ type: ActionType.SET_REPORT_INFO, data: requestBody });
      } else {
        downloadDetailedReport();
      }
    }
  };

  const goToIndividualSitePicker = () => {
    if (validateFields()) {
      const postData: IndividualSiteRequestProps = {
        cuId: getCustomerInfoFromAPIResponse(customer)?.customerid,
        reportType: 'getIndividualInstitutionInfo',
        requestBody: preparePostRequest(),
      };
      dispatch({ type: ActionType.SET_INDIVIDUAL_SITE_PAGE_INFO, data: postData });
    }
  };

  const updateProduct = useCallback(
    (value: SearchableDropdownValue[]) => {
      setSelectedProducts(value);
      setAllProductSelected(value.length === parsedProducts.length);
    },
    [parsedProducts],
  );

  const onProductSelectCallback = useCallback(
    (selectedValue: SearchableDropdownValue[]) => {
      let selectedProducts = filter(selectedValue, ['isSelected', true]);
      updateProduct(selectedProducts);
    },
    [updateProduct],
  );

  const downloadDetailedReport = async () => {
    if (validateFields()) {
      const requestBody = preparePostRequest();
      setReportLoading(true);

      try {
        await downloadCsv(requestBody);

        setTimeout(() => {
          let element: HTMLElement = document.querySelector(
            '.stats-home-section__export-btn',
          ) as HTMLElement;

          if (element) {
            element.click();
            setDetailedCsvData([]);
            setReportLoading(false);
          }
        }, 0);
      } catch (error) {
        notify.warning('Something went wrong, please try again later');
        setReportLoading(false);
      }
    }
  };

  const downloadCsv = async (reportParams: any): Promise<void> => {
    const requestObj: ChartApiRequest = {
      request: reportParams?.reportType,
      cuid: reportParams?.cuId,
      startDate: reportParams.startDate?.toLocaleDateString('fr-CA'),
      endDate: reportParams.endDate?.toLocaleDateString('fr-CA'),
      product: reportParams?.product,
      customer: reportParams?.customer,
      groups: reportParams?.groups,
      byProduct: reportParams?.byProduct,
      valueKey: reportParams?.valueKey,
    };

    const rawCharttData: ChartApiResponse = await fetchChartData(requestObj);

    const formattedChartData: TransformChartDataResponse =
      reportParams?.reportType === 'documentUsageByGroup' ||
      reportParams?.reportType === 'sessionUsageByGroup'
        ? transformGroupedChartData(rawCharttData)
        : transformChartData(rawCharttData, reportParams?.reportType, reportParams?.productName);

    const formattedTableData = transformTableData(formattedChartData?.totalChartobj);
    const formattedCsvData: (string | number)[][] = [
      [reportType],
      [reportParams.productName],
      ['Total'],
      ['', ...formattedTableData.columns],
    ];
    formattedTableData.rows.map((row) => {
      const rowData: (string | number)[] = [row.name, ...row.values];
      formattedCsvData.push(rowData);
      return {};
    });

    setDetailedCsvData(formattedCsvData);
  };

  if (state.reportInfo && (reportType === 'UGR' || reportType === 'DUBG' || reportType === 'SUBG'))
    return <Redirect to="/user-group-report" />;

  if (state.reportInfo && reportType === 'OSR') return <Redirect to="/overall-summary-report" />;

  if (
    state.reportInfo &&
    reportType !== 'OSR' &&
    reportType !== 'UGR' &&
    reportType !== 'SRISR' &&
    reportType !== 'DUBG' &&
    reportType !== 'SUBG'
  )
    return <Redirect to="/report-charts" />;

  if (state.individualSiteInfo && reportType === 'SRISR')
    return <Redirect to="/individual-site-reports" />;

  if (state.individualSiteInfo && reportType === 'ISR') return <Redirect to="/stat-report" />;

  if (!state?.isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div className={classNames('stats-home', reportLoading && 'stats-home--blur')}>
      {reportLoading && <PageLoader color={'dark'} size={'large'} type={'border'} />}

      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />
      <div className="stats-home__section">
        <ReportSelector
          isGroup={isGroup}
          hasChildren={hasChildren}
          onReportTypeChange={(selectedValue: TypeOfReport) => {
            setReportType(selectedValue);
            updateProduct([]);
          }}
          defultReport={reportType}
        />
      </div>
      <div className="stats-home__section">
        <ProductSelector
          availableProducts={parsedProducts}
          reportType={reportType}
          allSelected={allProductSelected}
          onProductChange={onProductSelectCallback}
        />
      </div>
      <div className="stats-home__section">
        <div className="stats-home__section-element">
          <DateSelector
            availableDates={parsedDates}
            reportType={reportType}
            onDateSelect={onDateSelectCallback}
            OnStartDateSelect={(selectedValue: string | undefined) => {
              setSelectedStartDate(selectedValue);
            }}
          />
        </div>

        <div className="stats-home__section-element">
          <SiteSelector
            reportType={reportType}
            defaultOutputType={outputType}
            onCheckboxSelection={setIsIndividualReportCallback}
            onReportTypeSelection={onReportTypeSelectionCallback}
          />
        </div>

        <div className="stats-home__section-element">
          {reportType !== 'ISR' &&
            isDetailedReport &&
            reportType !== 'DUBG' &&
            reportType !== 'SUBG' &&
            detailedCsvData.length === 0 && (
              <Button color="info" id="next" className="float-right mt-2" onClick={createReport}>
                Create Report
              </Button>
            )}

          {detailedCsvData.length > 0 &&
            outputType === 'Csv' &&
            reportType !== 'ISR' &&
            reportType !== 'DUBG' &&
            reportType !== 'SUBG' &&
            isDetailedReport && (
              <CSVLink
                className="stats-home-section__export-btn float-right mt-2 vol-button vol-button--info"
                data={detailedCsvData}
                filename={`${reportType}.csv`}
              >
                Create Report
              </CSVLink>
            )}

          {reportType === 'ISR' && (
            <Button
              color="info"
              className="float-right mt-2"
              id="next"
              onClick={goToIndividualSitePicker}
            >
              Next
            </Button>
          )}

          {(reportType === 'UGR' || reportType === 'DUBG' || reportType === 'SUBG') && (
            <Button color="info" id="next" className="float-right mt-2" onClick={createReport}>
              Next
            </Button>
          )}

          {reportType === 'SRISR' && (
            <Button
              color="info"
              id="next"
              className="float-right mt-2"
              onClick={goToIndividualSitePicker}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
